import React from 'react';
import {Button, Table, Tag} from 'antd';
import type {TableColumnsType} from 'antd';
import {useTranslation} from 'react-i18next';
import {formatEpochToDateTime} from '../../../../utils/dateTimeUtils';
import {PopReviewStatus, PopReviewType, isPopReviewFinished} from '../graphql/types/PopReviewType';

interface PopReviewTableProps {
    popReviewData: PopReviewType[];
    loading: boolean;
    /** 打开详情 Drawer（批量审核也由此入口逐条推进） */
    showDetail: (record: PopReviewType) => void;
    onSelectChange: (selectedRowKeys: React.Key[], selectedRows: PopReviewType[]) => void;
    selectedRowKeys: React.Key[];
    /** Hub ID → 名称；未命中时兜底显示 ID */
    hubNameById: Record<string, string>;
    /** 站点 ID → 名称；未命中回落显示 ID */
    pickupSiteNameById: Record<string, string>;
    /** 司机 account ID → 姓名；未命中回落显示 ID */
    pickupDriverNameById: Record<string, string>;
    /**
     * 是否显示批量选择复选框。已终止单不可再审核，故「已终止」Tab 不需要选择。
     * 注意：操作列（查看/审核）始终显示，与本开关无关。
     */
    showRowSelection?: boolean;
}

/** 状态对应的 Tag 颜色：待审核中性、合格绿、疑似虚假红、已取消灰 */
const STATUS_COLOR: Record<PopReviewStatus, string> = {
    [PopReviewStatus.PENDING_REVIEW]: 'processing',
    [PopReviewStatus.APPROVED]: 'success',
    [PopReviewStatus.REJECTED]: 'error',
    [PopReviewStatus.CANCELLED]: 'default'
};

/**
 * 揽收状态 code → i18n key；复用揽收预约单页的文案，避免同一状态两页不一致。
 * key 是 camelCase 而 code 是 SNAKE_CASE，故需显式映射。
 */
const PICKUP_STATUS_I18N_KEY: Record<string, string> = {
    CREATED: 'created',
    DISPATCHING: 'dispatching',
    DISPATCHED: 'dispatched',
    PICKING_UP: 'pickingUp',
    PICKED_UP: 'pickedUp',
    PICKUP_FAILED: 'pickupFailed',
    CANCELLED: 'cancelled'
};

const PopReviewTable: React.FC<PopReviewTableProps> = ({
    popReviewData,
    loading,
    showDetail,
    onSelectChange,
    selectedRowKeys,
    hubNameById,
    pickupSiteNameById,
    pickupDriverNameById,
    showRowSelection = true
}) => {
    const {t} = useTranslation();

    /** 审核结论文案：后端为引擎态，展示语义在前端转换（APPROVED→合格，REJECTED→疑似虚假） */
    const getStatusText = (status?: PopReviewStatus) =>
        status ? t(`popReview.enum.status.${status}`) : t('popReview.enum.status.UNKNOWN');

    const columns: TableColumnsType<PopReviewType> = [
        {
            title: t('popReview.table.appointmentNumber'),
            dataIndex: 'appointmentNumber',
            key: 'appointmentNumber',
            render: (text?: string, record?: PopReviewType) => (
                <Button type="link" style={{padding: 0}} onClick={() => record && showDetail(record)}>
                    {text || record?.reviewTaskId || '-'}
                </Button>
            )
        },
        {
            title: t('popReview.table.status'),
            dataIndex: 'status',
            key: 'status',
            render: (status?: PopReviewStatus) => (
                <Tag color={status ? STATUS_COLOR[status] : 'default'}>{getStatusText(status)}</Tag>
            )
        },
        {
            title: t('popReview.table.pickupHub'),
            dataIndex: 'pickupHubId',
            key: 'pickupHubId',
            // 优先用后端 DataLoader 回查的名称；其次筛选区下拉映射；都没有才回落 ID
            render: (hubId?: string, record?: PopReviewType) =>
                record?.pickupHubName || (hubId ? hubNameById[hubId] || hubId : '-')
        },
        {
            title: t('popReview.table.pickupSiteId'),
            dataIndex: 'pickupSiteId',
            key: 'pickupSiteId',
            render: (siteId?: string) => siteId || '-'
        },
        {
            title: t('popReview.table.pickupSite'),
            key: 'pickupSiteName',
            // 优先用揽收侧回查的名称（不依赖是否选过 Hub）；其次下拉映射；都没有回落 ID
            render: (_: unknown, record: PopReviewType) =>
                record.pickupDetail?.siteName ||
                (record.pickupSiteId
                    ? pickupSiteNameById[record.pickupSiteId] || record.pickupSiteId
                    : '-')
        },
        {
            title: t('popReview.table.driver'),
            key: 'driver',
            // 三级兜底：后端 DataLoader 回查的姓名 → 筛选区下拉映射 → 回落显示 ID。
            // 下拉只覆盖当前 Hub 下的在职司机，故不能只依赖它
            render: (_: unknown, record: PopReviewType) =>
                record.driverName ||
                (record.driverId
                    ? pickupDriverNameById[record.driverId] || record.driverId
                    : '-')
        },
        {
            title: t('popReview.table.contactPhone'),
            key: 'contactPhone',
            render: (_: unknown, record: PopReviewType) => record.pickupDetail?.contactPhone || '-'
        },
        {
            title: t('popReview.table.channelName'),
            key: 'channelName',
            // 名称由后端 DataLoader 回查 channel；查不到回落显示 ID
            render: (_: unknown, record: PopReviewType) =>
                record.pickupDetail?.channelName || record.pickupDetail?.channelId || '-'
        },
        {
            title: t('popReview.table.address'),
            key: 'address',
            // 地址是唯一可能很长的列，保留截断并给出上限，其余列按内容自适应
            ellipsis: true,
            width: 240,
            render: (_: unknown, record: PopReviewType) => record.pickupDetail?.address || '-'
        },
        {
            title: t('popReview.table.source'),
            key: 'source',
            // 后端返原始枚举值，文案在前端映射（缺省显示原值，便于发现未收录的新枚举）
            render: (_: unknown, record: PopReviewType) =>
                record.pickupDetail?.source
                    ? t(`popReview.enum.source.${record.pickupDetail.source}`, {
                          defaultValue: record.pickupDetail.source
                      })
                    : '-'
        },
        {
            title: t('popReview.table.isNewWarehouse'),
            key: 'isNewWarehouse',
            render: (_: unknown, record: PopReviewType) =>
                record.pickupDetail?.isNewWarehouse == null
                    ? '-'
                    : t(record.pickupDetail.isNewWarehouse === 1 ? 'common.yes' : 'common.no')
        },
        {
            title: t('popReview.table.routeName'),
            key: 'routeName',
            render: (_: unknown, record: PopReviewType) => record.pickupDetail?.routeName || '-'
        },
        {
            title: t('popReview.table.zipcode'),
            key: 'zipcode',
            render: (_: unknown, record: PopReviewType) => record.pickupDetail?.zipcode || '-'
        },
        {
            title: t('popReview.table.expectedPickupTime'),
            key: 'expectedPickupTime',
            // 日期与开始时间分列存放，展示时拼成一个可读时间
            render: (_: unknown, record: PopReviewType) => {
                const date = record.pickupDetail?.expectedPickupDate;
                const time = record.pickupDetail?.expectedPickupTimeStart;
                if (!date) return '-';
                return time ? `${date} ${time.slice(0, 5)}` : date;
            }
        },
        {
            title: t('popReview.table.timezone'),
            key: 'timezone',
            render: (_: unknown, record: PopReviewType) => record.pickupDetail?.timezone || '-'
        },
        {
            title: t('popReview.table.actualParcelCount'),
            dataIndex: 'actualParcelCount',
            key: 'actualParcelCount',
            render: (count?: number) => (count || '-')
        },
        {
            title: t('popReview.table.pickupStatus'),
            key: 'pickupStatus',
            render: (_: unknown, record: PopReviewType) => {
                const code = record.pickupDetail?.pickupStatus;
                if (!code) return '-';
                const key = PICKUP_STATUS_I18N_KEY[code];
                // 未收录的新枚举回落显示原始 code，便于发现字典缺项
                return key ? t(`enum.pickupAppointmentStatus.${key}`) : code;
            }
        },
        {
            title: t('popReview.table.taskNumber'),
            key: 'taskNumber',
            render: (_: unknown, record: PopReviewType) => record.pickupDetail?.taskNumber || '-'
        },
        {
            title: t('popReview.table.reviewedAt'),
            key: 'reviewedAt',
            // 待审核单尚无审核时间，formatEpochToDateTime 对空值兜底为 '-'
            render: (_: unknown, record: PopReviewType) =>
                formatEpochToDateTime(record.reviewedAt)
        },
        {
            // 「更新时间」取 pop_review.updated_at，与列表排序字段同源
            title: t('popReview.table.updatedAt'),
            key: 'updatedAt',
            render: (_: unknown, record: PopReviewType) =>
                formatEpochToDateTime(record.updatedAt)
        }
    ];

    // 操作列始终存在：已终止单不可再审核，但必须能查看详情（照片 / 日志 / 各轮原因）。
    // 按钮文案依「行的实际状态」而非当前 Tab —— 用户可同时用 statuses 筛选，两者未必对应。
    columns.push({
        title: t('popReview.table.action'),
        key: 'action',
        fixed: 'right' as const,
        render: (_: unknown, record: PopReviewType) => (
            <Button type="link" style={{padding: 0}} onClick={() => showDetail(record)}>
                {isPopReviewFinished(record.status)
                    ? t('popReview.table.view')
                    : t('popReview.table.review')}
            </Button>
        )
    });

    return (
        <Table<PopReviewType>
            rowKey="reviewTaskId"
            columns={columns}
            dataSource={popReviewData}
            loading={loading}
            pagination={false}
            // max-content + 表头不折行：列宽由内容决定，横向溢出时滚动。
            // 不写死 width 才能让「实际包裹数」这类较长表头保持一行（对齐 whiteSpace 设置）
            scroll={{x: 'max-content'}}
            className="[&_.ant-table-thead_th]:whitespace-nowrap [&_.ant-table-tbody_td]:whitespace-nowrap"
            rowSelection={
                showRowSelection
                    ? {
                          selectedRowKeys,
                          onChange: onSelectChange
                      }
                    : undefined
            }
        />
    );
};

export default PopReviewTable;
